/**
 * leadSourceRisk — pure, read-only selector that determines whether a given
 * lead source has historically required schedule changes often enough to
 * warrant a subtle informational hint at the moment of sale.
 *
 * Phase B v1 scope (approved):
 *   • Reschedules-only signal (not extensions, not conflicts).
 *   • Source resolution: job.opportunityId → opportunity.leadSource,
 *     fallback to job.marketing?.leadSource. Jobs with neither are excluded.
 *   • Reschedule detection mirrors outcomeMetrics.ts:
 *       /reschedul/i.test(activityHistory[].action)
 *   • 90-day window keyed on job.scheduledInstallDate (matches the existing
 *     Reschedule Rate KPI window — single window definition).
 *
 * Triple-gated thresholds — ALL must be true to surface:
 *   1. N ≥ 3 jobs from that source within the window
 *   2. source reschedule rate ≥ 30%
 *   3. source reschedule rate ≥ 1.5× platform-overall reschedule rate
 *
 * No new engine, no scoring system, no persistence. Pure derivation from
 * existing seed/runtime data.
 */
import { parseISO, isValid, subDays } from "date-fns";
import type { Job } from "./types";
import type { Opportunity } from "./opportunityTypes";

const WINDOW_DAYS = 90;
const MIN_SAMPLE = 3;
const MIN_RATE = 0.3;
const MIN_RATIO_OVER_PLATFORM = 1.5;

export interface LeadSourceRescheduleRisk {
  /** Source under evaluation (echoed for caller convenience). */
  leadSource: string;
  /** Number of windowed jobs from this source. */
  n: number;
  /** Reschedule rate for this source (0–100, integer). */
  rate: number;
  /** Whether all three gates passed. Callers should branch on this. */
  shouldShow: boolean;
}

function jobHasReschedule(job: Job): boolean {
  return (job.activityHistory || []).some((a) =>
    /reschedul/i.test(a.action || ""),
  );
}

function resolveLeadSource(
  job: Job,
  oppByJobId: Map<string, Opportunity>,
  oppById: Map<string, Opportunity>,
): string | undefined {
  // Primary: explicit forward link from job → opportunity.
  if (job.opportunityId) {
    const opp = oppById.get(job.opportunityId);
    if (opp?.leadSource) return opp.leadSource;
  }
  // Secondary: opportunity that points back to this job.
  const linked = oppByJobId.get(job.id);
  if (linked?.leadSource) return linked.leadSource;
  // Fallback: job-level marketing attribution.
  if (job.marketing?.leadSource) return job.marketing.leadSource;
  return undefined;
}

/**
 * Compute the lead-source reschedule risk signal for a single source.
 * Returns null if the source itself is empty/unknown — callers should treat
 * `null` and `{ shouldShow: false }` identically (do not render).
 */
export function getLeadSourceRescheduleRisk(
  leadSource: string | undefined | null,
  jobs: Job[],
  opportunities: Opportunity[],
  referenceDate: Date = new Date(),
): LeadSourceRescheduleRisk | null {
  if (!leadSource) return null;

  const windowStart = subDays(referenceDate, WINDOW_DAYS);
  const oppById = new Map<string, Opportunity>();
  const oppByJobId = new Map<string, Opportunity>();
  for (const o of opportunities) {
    oppById.set(o.id, o);
    if (o.linkedJobId) oppByJobId.set(o.linkedJobId, o);
  }

  let sourceN = 0;
  let sourceResched = 0;
  let platformN = 0;
  let platformResched = 0;

  for (const job of jobs) {
    if (!job.scheduledInstallDate) continue;
    const d = parseISO(job.scheduledInstallDate);
    if (!isValid(d) || d < windowStart || d > referenceDate) continue;

    const src = resolveLeadSource(job, oppByJobId, oppById);
    if (!src) continue; // honest exclusion — no source, no contribution

    const rescheduled = jobHasReschedule(job);
    platformN += 1;
    if (rescheduled) platformResched += 1;

    if (src === leadSource) {
      sourceN += 1;
      if (rescheduled) sourceResched += 1;
    }
  }

  if (sourceN === 0) return null;

  const sourceRate = sourceResched / sourceN;
  const platformRate = platformN > 0 ? platformResched / platformN : 0;

  const passesSample = sourceN >= MIN_SAMPLE;
  const passesAbsolute = sourceRate >= MIN_RATE;
  const passesRelative =
    platformRate > 0 && sourceRate >= platformRate * MIN_RATIO_OVER_PLATFORM;

  return {
    leadSource,
    n: sourceN,
    rate: Math.round(sourceRate * 100),
    shouldShow: passesSample && passesAbsolute && passesRelative,
  };
}
